import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaProjectDiagram,
  FaRupeeSign,
  FaUsers,
  FaClipboardList,
  FaPlus,
  FaChartLine,
  FaBell,
} from "react-icons/fa";
import { MdDashboard } from "react-icons/md";
import useAuth from "../useAuth";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

export default function Homepage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schemes, setSchemes] = useState([]);
  const [schemeStats, setSchemeStats] = useState({});
  const [districtStats, setDistrictStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSchemes = async () => {
    try {
      setLoading(true);

      const { data: schemesData } = await axios.get(`${server}/scheme/all`, {
        params: { pageNo: 1, pageSize: 5 }, // Reduced page size for compact view
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setSchemes(schemesData?.data || []);

      const { data: reportData } = await axios.get(`${server}/scheme/report`, {
        params: { pageNo: 1, pageSize: 1000 },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (reportData?.success) {
        setSchemeStats(reportData.data.totalStats);
        setDistrictStats(reportData.data.districtWiseStats || []);
      }
    } catch (error) {
      console.error("Error fetching schemes:", error);
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  const statusData = [
    { name: "Completed", value: schemeStats?.totalCompletedSchemes || 0, color: "#10B981" },
    { name: "Draft", value: schemeStats?.totalDraftSchemes || 0, color: "#3B82F6" },
  ];

  // Filter districts with schemes for better visualization
  const activeDistricts = districtStats
    .filter(d => d.totalSchemes > 0)
    .sort((a, b) => b.totalSchemes - a.totalSchemes);

  const stats = [
    {
      title: "Total Schemes",
      value: schemeStats?.totalSchemes || 0,
      icon: <FaProjectDiagram className="text-lg text-blue-600" />,
      bg: "bg-blue-50",
      border: "border-blue-100",
    },
    {
      title: "Completed",
      value: schemeStats?.totalCompletedSchemes || 0,
      icon: <FaClipboardList className="text-lg text-emerald-600" />,
      bg: "bg-emerald-50",
      border: "border-emerald-100",
    },
    {
      title: "Drafts",
      value: schemeStats?.totalDraftSchemes || 0,
      icon: <FaUsers className="text-lg text-amber-600" />,
      bg: "bg-amber-50",
      border: "border-amber-100",
    },
    {
      title: "Fund Allocation",
      value: `₹${((schemeStats?.totalAllocationAmount || 0) / 10000000).toFixed(2)} Cr`,
      icon: <FaRupeeSign className="text-lg text-rose-600" />,
      bg: "bg-rose-50",
      border: "border-rose-100",
    },
  ];

  return (
    <motion.div
      className="min-h-screen"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >


      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Left Column: Stats & Recent Table */}
        <div className="lg:col-span-8 space-y-6">

          {/* Compact Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`bg-white rounded-2xl p-4 border ${stat.border} shadow-sm hover:shadow-md transition-all duration-300 group`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 ${stat.bg} rounded-xl group-hover:scale-110 transition-transform`}>
                    {stat.icon}
                  </div>
                  <div>
                    <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">{stat.title}</p>
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">
                      {stat.value}
                    </h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {user?.userType === "admin" &&
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-bold text-slate-800">District-wise Performance</h2>
                <div className="flex gap-2">
                  <span className="flex items-center gap-1.5 text-[10px] font-medium text-slate-500">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span> Schemes
                  </span>
                </div>
              </div>

              <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={activeDistricts.slice(0, 10)} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="district"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      interval={0}
                    />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#64748b" }} />
                    <Tooltip
                      cursor={{ fill: '#f8fafc' }}
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                    />
                    <Bar dataKey="totalSchemes" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          }

          {/* Recent Schemes Table - Compact */}
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
          >
            <div className="p-5 flex justify-between items-center border-b border-slate-100">
              <h2 className="font-bold text-slate-800">Recent Schemes</h2>
              <button
                onClick={() => navigate("/my/schemesList")}
                className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition"
              >
                View All
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100">
                    <th className="px-5 py-3 text-left">Scheme Name</th>
                    <th className="px-5 py-3 text-left">Status</th>
                    <th className="px-5 py-3 text-left">Sector</th>
                    <th className="px-5 py-3 text-right">Allocated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {schemes.map((scheme, index) => (
                    <tr
                      key={index}
                      className="hover:bg-slate-50/80 cursor-pointer transition"
                      onClick={() => {
                        scheme?.formStatus === "completed"
                          ? navigate("../schemeDetails", { state: { schemeId: scheme?.id } })
                          : navigate("../addNewScheme", {
                            state: { draftSchemeId: scheme?.id, draftSchemeData: scheme },
                          });
                      }}
                    >
                      <td className="px-5 py-3 font-semibold text-slate-800 max-w-[200px] truncate">
                        {scheme.schemeName || "---"}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${scheme.formStatus === "completed"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-blue-100 text-blue-700"
                            }`}
                        >
                          {scheme?.formStatus}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-500">{scheme?.sector || "General"}</td>
                      <td className="px-5 py-3 text-right font-bold text-slate-900">
                        ₹{(scheme?.allocatedAmount || 0).toLocaleString("en-In")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>

        {/* Right Column: Status Chart & Quick Actions */}
        <div className="lg:col-span-4 space-y-6">
          {/* Status Breakdown Chart - Admin Only */}
          {user?.userType === "admin" && (
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5"
            >
              <h2 className="font-bold text-slate-800 mb-4">Scheme Status</h2>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      innerRadius={35}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}

          {/* Quick Actions - Visible to All */}
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5"
          >
            <h2 className="font-bold text-slate-800 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={() => navigate("/my/addNewScheme")}
                className="flex items-center gap-3 p-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-md shadow-blue-100 group"
              >
                <div className="p-2 bg-white/20 rounded-lg group-hover:scale-110 transition">
                  <FaPlus className="text-white" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold">New Scheme</p>
                  <p className="text-[10px] text-blue-100">Create a new entry</p>
                </div>
              </button>

              {user?.userType === "admin" && (
                <button
                  onClick={() => navigate("/my/report")}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all group"
                >
                  <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-500 group-hover:text-white transition">
                    <FaChartLine className="text-emerald-600 group-hover:text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-slate-800">Analytics</p>
                    <p className="text-[10px] text-slate-500">View detailed reports</p>
                  </div>
                </button>
              )}

              <button
                onClick={() => navigate("/my/profile")}
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-all group"
              >
                <div className="p-2 bg-amber-100 rounded-lg group-hover:bg-amber-500 group-hover:text-white transition">
                  <FaUsers className="text-amber-600 group-hover:text-white" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-slate-800">Profile</p>
                  <p className="text-[10px] text-slate-500">Manage account</p>
                </div>
              </button>
            </div>
          </motion.div>


        </div>

      </div>
    </motion.div>
  );
}
